import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ColorSlider from './ColorSlider';
import Timer from './Timer';
import ScoreDisplay from './ScoreDisplay';
import GameOverModal from './GameOverModal';
import HintModal from './HintModal';
import AchievementModal from './AchievementModal';
import { AchievementManager } from '../utils/achievementManager';

const { FiArrowLeft, FiHelpCircle, FiCheck, FiX, FiTarget, FiZap } = FiIcons;

function GameScreen({ onBack, settings }) {
  const [targetColor, setTargetColor] = useState({ r: 0, g: 0, b: 0 });
  const [userColor, setUserColor] = useState({ r: 128, g: 128, b: 128 });
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [streak, setStreak] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [isGameActive, setIsGameActive] = useState(true);
  const [gameOver, setGameOver] = useState(false);
  const [accuracy, setAccuracy] = useState(0);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [roundsPlayed, setRoundsPlayed] = useState(0);
  const [newAchievements, setNewAchievements] = useState([]);
  const [currentAchievementIndex, setCurrentAchievementIndex] = useState(0);
  const [roundStartTime, setRoundStartTime] = useState(Date.now());

  const difficulties = {
    easy: { tolerance: 30, timeBonus: 1.2 },
    medium: { tolerance: 20, timeBonus: 1.5 },
    hard: { tolerance: 10, timeBonus: 2.0 }
  };

  const generateTargetColor = useCallback(() => {
    const r = Math.floor(Math.random() * 256);
    const g = Math.floor(Math.random() * 256);
    const b = Math.floor(Math.random() * 256);
    setTargetColor({ r, g, b });
    setRoundStartTime(Date.now());
  }, []);

  const calculateAccuracy = useCallback(() => {
    const rDiff = Math.abs(targetColor.r - userColor.r);
    const gDiff = Math.abs(targetColor.g - userColor.g);
    const bDiff = Math.abs(targetColor.b - userColor.b);
    const totalDiff = rDiff + gDiff + bDiff;
    const maxDiff = 765; // 255 * 3
    const acc = Math.max(0, 100 - (totalDiff / maxDiff) * 100);
    setAccuracy(acc);
    return acc;
  }, [targetColor, userColor]);

  const calculateScore = (acc, timeBonus = 1) => {
    let baseScore = Math.floor(acc * level * timeBonus);
    
    // High accuracy bonuses
    if (acc >= 98) baseScore += 100;
    else if (acc >= 95) baseScore += 50;
    
    // Hint penalty
    baseScore -= hintsUsed * 10;
    
    return Math.max(0, baseScore);
  };

  const handleSubmit = () => {
    const acc = calculateAccuracy();
    const timeBonus = timeLeft > 20 ? 1.5 : timeLeft > 15 ? 1.25 : 1;
    const roundScore = calculateScore(acc, timeBonus);
    const roundTime = Date.now() - roundStartTime;
    const wasQuickAnswer = roundTime < 10000; // Under 10 seconds
    
    setScore(prev => prev + roundScore);
    
    const tolerance = difficulties[settings.difficulty].tolerance;
    const isCorrect = acc >= (100 - tolerance);
    
    if (isCorrect) {
      setStreak(prev => prev + 1);
      setLevel(prev => Math.floor(prev + 1 + streak / 5));
      setTimeLeft(prev => Math.min(30, prev + 5));
      setFeedback(`Excellent! +${roundScore} points`);
    } else {
      setStreak(0);
      setTimeLeft(prev => Math.max(0, prev - 3));
      setFeedback(`Close! ${acc.toFixed(1)}% accuracy`);
    }
    
    setRoundsPlayed(prev => prev + 1);

    // Check for achievements
    const gameData = {
      score: score + roundScore,
      accuracy: acc,
      streak: isCorrect ? streak + 1 : 0,
      level: level,
      hintsUsed,
      wasQuickAnswer,
      isWin: isCorrect,
      difficulty: settings.difficulty,
      gameMode: settings.gameMode
    };

    const playerStats = AchievementManager.updatePlayerStats(gameData);
    const achievements = AchievementManager.checkAchievements(gameData, playerStats);
    
    if (achievements.length > 0) {
      setNewAchievements(achievements);
      setCurrentAchievementIndex(0);
    }
    
    setTimeout(() => {
      generateTargetColor();
      setUserColor({ r: 128, g: 128, b: 128 });
      setHintsUsed(0);
      setFeedback('');
    }, 1500);
  };

  const handleTimeUp = () => {
    setIsGameActive(false);
    setGameOver(true);
  };

  const handleHint = () => {
    setHintsUsed(prev => prev + 1);
    setShowHint(true);
  };

  const showNextAchievement = () => {
    if (currentAchievementIndex < newAchievements.length - 1) {
      setCurrentAchievementIndex(prev => prev + 1);
    } else {
      setNewAchievements([]);
      setCurrentAchievementIndex(0);
    }
  };

  useEffect(() => {
    generateTargetColor();
  }, [generateTargetColor]);

  useEffect(() => {
    calculateAccuracy();
  }, [calculateAccuracy]);

  useEffect(() => {
    if (settings.gameMode === 'endless') {
      setTimeLeft(Infinity);
      return;
    }

    if (!isGameActive || timeLeft <= 0) {
      if (timeLeft <= 0) handleTimeUp();
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isGameActive, timeLeft, settings.gameMode]);

  const targetRgb = `rgb(${targetColor.r}, ${targetColor.g}, ${targetColor.b})`;
  const userRgb = `rgb(${userColor.r}, ${userColor.g}, ${userColor.b})`;

  return (
    <div className="min-h-screen p-4 pb-8">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onBack}
            className="bg-white/20 backdrop-blur-sm p-3 rounded-xl"
          >
            <SafeIcon icon={FiArrowLeft} className="text-xl text-white" />
          </motion.button>
          
          <div className="text-center">
            <h2 className="text-white font-bold text-lg">Level {level}</h2>
            <p className="text-gray-300 text-sm">Streak: {streak}</p>
          </div>
          
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleHint}
            className="bg-yellow-500/20 backdrop-blur-sm p-3 rounded-xl"
          >
            <SafeIcon icon={FiHelpCircle} className="text-xl text-yellow-400" />
          </motion.button>
        </div>

        {/* Timer */}
        {settings.gameMode === 'timed' && (
          <Timer timeLeft={timeLeft} totalTime={30} />
        )}

        {/* Score Display */}
        <ScoreDisplay 
          score={score} 
          accuracy={accuracy} 
          level={level} 
          streak={streak}
        />

        {/* Color Comparison */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-6">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="text-center">
              <p className="text-white text-sm mb-2 flex items-center justify-center gap-2">
                <SafeIcon icon={FiTarget} className="text-yellow-400" />
                Target
              </p>
              <div 
                className="w-full h-24 rounded-xl border-2 border-white/20"
                style={{ backgroundColor: targetRgb }}
              />
              <p className="text-gray-300 text-xs mt-2">
                RGB({targetColor.r}, {targetColor.g}, {targetColor.b})
              </p>
            </div>
            
            <div className="text-center">
              <p className="text-white text-sm mb-2 flex items-center justify-center gap-2">
                <SafeIcon icon={FiZap} className="text-blue-400" />
                Your Mix
              </p>
              <div 
                className="w-full h-24 rounded-xl border-2 border-white/20"
                style={{ backgroundColor: userRgb }}
              />
              <p className="text-gray-300 text-xs mt-2">
                RGB({userColor.r}, {userColor.g}, {userColor.b})
              </p>
            </div>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold text-white mb-2">
              {accuracy.toFixed(1)}% Match
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <motion.div 
                className="bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${accuracy}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
        </div>

        {/* RGB Sliders */}
        <div className="space-y-4 mb-6">
          <ColorSlider
            label="Red"
            value={userColor.r}
            onChange={(value) => setUserColor(prev => ({ ...prev, r: value }))}
            color="red"
          />
          <ColorSlider
            label="Green"
            value={userColor.g}
            onChange={(value) => setUserColor(prev => ({ ...prev, g: value }))}
            color="green"
          />
          <ColorSlider
            label="Blue"
            value={userColor.b}
            onChange={(value) => setUserColor(prev => ({ ...prev, b: value }))}
            color="blue"
          />
        </div>

        {/* Submit Button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleSubmit}
          disabled={!isGameActive}
          className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold py-4 rounded-2xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex items-center justify-center gap-2">
            <SafeIcon icon={FiCheck} className="text-xl" />
            Submit Color Mix
          </div>
        </motion.button>

        {/* Feedback */}
        <AnimatePresence>
          {feedback && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mt-4 text-center"
            >
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
                <p className="text-white font-medium">{feedback}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Modals */}
      <GameOverModal
        isOpen={gameOver}
        score={score}
        level={level}
        streak={streak}
        rounds={roundsPlayed}
        onRestart={() => {
          setGameOver(false);
          setScore(0);
          setLevel(1);
          setStreak(0);
          setTimeLeft(30);
          setIsGameActive(true);
          setRoundsPlayed(0);
          generateTargetColor();
        }}
        onMenu={onBack}
        settings={settings}
      />

      <HintModal
        isOpen={showHint}
        onClose={() => setShowHint(false)}
        targetColor={targetColor}
        userColor={userColor}
      />

      <AchievementModal
        isOpen={newAchievements.length > 0}
        onClose={showNextAchievement}
        achievement={newAchievements[currentAchievementIndex]}
      />
    </div>
  );
}

export default GameScreen;